import { useState, useMemo } from 'react';

import { X } from 'lucide-react';

import { Alert } from '~/components/ui/alert';
import { Button } from '~/components/ui/button';
import { cn } from '~/lib/utils';

export interface TrialBannerProps {
  trialEndsAt: string | null;
  onUpgrade: () => void;
  onViewPlans: () => void;
  onDismiss?: () => void;
}

const DISMISS_STORAGE_KEY = 'trial-banner-dismissed-at';
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const DAYS_THRESHOLD = 7;

function calculateDaysRemaining(trialEndsAt: string): number | null {
  try {
    const endDate = new Date(trialEndsAt);
    const now = new Date();

    if (isNaN(endDate.getTime())) {
      return null;
    }

    const diffMs = endDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    return diffDays;
  } catch {
    return null;
  }
}

function isDismissed(): boolean {
  try {
    const dismissedAt = localStorage.getItem(DISMISS_STORAGE_KEY);

    if (!dismissedAt) {
      return false;
    }

    const dismissedTime = parseInt(dismissedAt, 10);

    if (isNaN(dismissedTime)) {
      return false;
    }

    const now = Date.now();
    const elapsed = now - dismissedTime;

    return elapsed < DISMISS_DURATION_MS;
  } catch {
    return false;
  }
}

function setDismissed(): void {
  try {
    localStorage.setItem(DISMISS_STORAGE_KEY, Date.now().toString());
  } catch {
    // Silently fail if localStorage unavailable
  }
}

function getSeverity(daysRemaining: number): 'default' | 'warning' | 'destructive' {
  if (daysRemaining < 0) {
    return 'destructive';
  }

  if (daysRemaining <= 3) {
    return 'warning';
  }

  return 'default';
}

function getMessage(daysRemaining: number): string {
  if (daysRemaining < 0) {
    return 'Your trial has ended';
  }

  if (daysRemaining === 0) {
    return 'Your trial ends today';
  }

  if (daysRemaining === 1) {
    return 'Your trial ends in 1 day';
  }

  return `Your trial ends in ${daysRemaining} days`;
}

export function TrialBanner({ trialEndsAt, onUpgrade, onViewPlans, onDismiss }: TrialBannerProps) {
  const [isDismissedByUser, setIsDismissedByUser] = useState<boolean>(false);

  const daysRemaining = useMemo<number | null>(() => {
    if (trialEndsAt === null) {
      return null;
    }

    return calculateDaysRemaining(trialEndsAt);
  }, [trialEndsAt]);

  const isVisible = useMemo<boolean>(() => {
    if (isDismissedByUser) {
      return false;
    }

    if (daysRemaining === null) {
      return false;
    }

    if (daysRemaining > DAYS_THRESHOLD) {
      return false;
    }

    if (isDismissed()) {
      return false;
    }

    return true;
  }, [daysRemaining, isDismissedByUser]);

  const handleDismiss = (): void => {
    setDismissed();
    setIsDismissedByUser(true);
    onDismiss?.();
  };

  if (!isVisible || daysRemaining === null) {
    return null;
  }

  const severity = getSeverity(daysRemaining);
  const message = getMessage(daysRemaining);

  return (
    <Alert
      data-testid="trial-banner"
      variant={severity}
      role={severity === 'destructive' ? 'alert' : undefined}
      className={cn('mb-4 flex flex-wrap items-center justify-between gap-4')}
    >
      <div className="flex flex-wrap items-center gap-4">
        <span>{message}</span>
        <Button size="sm" variant="default" onClick={onUpgrade}>
          Upgrade
        </Button>
        <Button size="sm" variant="outline" onClick={onViewPlans}>
          View Plans
        </Button>
      </div>
      <Button
        variant="ghost"
        size="icon"
        aria-label="Dismiss trial reminder"
        onClick={handleDismiss}
      >
        <X />
      </Button>
    </Alert>
  );
}
